import axios from "axios";

import { MAGNOLIA_BASE_URL } from "@/constants";
import { MAGNOLIA_LANG_PARAM, DEFAULT_I18N_CONFIG } from "@/libs/i18n/config";

const baseURL = MAGNOLIA_BASE_URL;

/**
 * Client-side axios instance for CMS requests
 * Automatically adds locale parameter
 *
 * NOTE: Only use this in client-side code (components with useEffect, event handlers, etc.)
 * For server-side usage, import from './axios-cms' instead
 */
const axiosCMSClient = axios.create({
  baseURL,
});

// Internal locale cache for client-side requests
// Initialized with default to prevent race conditions on first render
let cachedLocale: string = DEFAULT_I18N_CONFIG.defaultLocale;

/**
 * Set locale for axios interceptor (called from client components)
 * This allows us to use HttpOnly cookies while still having locale for API calls
 */
export function setAxiosCMSLocale(locale: string): void {
  cachedLocale = locale;
}

/**
 * Get cached locale (for use in non-React contexts like translation utilities)
 * Returns the locale set by AxiosLocaleInitializer, or the default if not yet initialized
 */
export function getCachedAxiosLocale(): string {
  return cachedLocale;
}

// Request interceptor to add lang parameter
axiosCMSClient.interceptors.request.use(
  (config) => {
    // Use cached locale (initialized with default, then updated by AxiosLocaleInitializer)
    const locale = cachedLocale;

    // Parse existing URL and add lang parameter
    const url = new URL(config.url!, config.baseURL);
    url.searchParams.set(MAGNOLIA_LANG_PARAM, locale);

    // Return modified config
    return {
      ...config,
      url: url.href,
    };
  },
  (error) => {
    // Ensure we're rejecting with an Error object
    if (error instanceof Error) {
      return Promise.reject(error);
    }
    return Promise.reject(new Error(String(error)));
  }
);

export default axiosCMSClient;
